import { useRef, useState } from 'react';
import { UploadCloud, FileCheck2, AlertTriangle, ClipboardPaste } from 'lucide-react';

const MAX_SIZE_BYTES = 16 * 1024; // un cert/key real pesa unos pocos KB

/**
 * Carga un archivo PEM (certificado o clave privada) por drag-and-drop o click,
 * con una validación de contenido liviana del lado del cliente (marcadores PEM +
 * tamaño razonable) para dar feedback inmediato y evitar errores obvios antes de
 * mandar nada al server. Esto NO reemplaza la validación real: el backend sigue
 * siendo quien verifica criptográficamente que la clave corresponda al
 * certificado (ver certificates.service.js) — acá solo filtramos basura.
 */
export default function FileDropInput({ label, expectedMarkers, value, onChange, hint, defaultPaste = false }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState(null);
  const [error, setError] = useState(null);
  const [pasteMode, setPasteMode] = useState(defaultPaste);

  const validate = (content) => {
    if (content.length > MAX_SIZE_BYTES) {
      return 'El archivo es demasiado grande para ser un certificado/clave válido';
    }
    if (!expectedMarkers.some((marker) => content.includes(marker))) {
      return `No parece un archivo PEM válido (se espera "${expectedMarkers[0]}")`;
    }
    return null;
  };

  const handleFile = (file) => {
    setError(null);
    if (file.size > MAX_SIZE_BYTES) {
      setError('El archivo es demasiado grande para ser un certificado/clave válido');
      setFileName(null);
      onChange('');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const content = String(reader.result || '');
      const err = validate(content);
      if (err) {
        setError(err);
        setFileName(null);
        onChange('');
        return;
      }
      setFileName(file.name);
      onChange(content);
    };
    reader.onerror = () => setError('No se pudo leer el archivo');
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handlePasteChange = (e) => {
    const content = e.target.value;
    onChange(content);
    setError(content ? validate(content) : null);
    setFileName(null);
  };

  if (pasteMode) {
    return (
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="label mb-0">{label}</label>
          <button type="button" onClick={() => setPasteMode(false)} className="text-xs text-accent hover:text-accent-hover">
            Subir archivo en su lugar
          </button>
        </div>
        <textarea
          className="input-base font-mono text-xs"
          rows={6}
          value={value}
          onChange={handlePasteChange}
          placeholder={`-----BEGIN ...-----`}
        />
        {error && <p className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertTriangle size={12} /> {error}</p>}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="label mb-0">{label}</label>
        <button type="button" onClick={() => setPasteMode(true)} className="text-xs text-text-muted hover:text-accent flex items-center gap-1">
          <ClipboardPaste size={12} /> Pegar texto
        </button>
      </div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 cursor-pointer transition-colors
          ${dragOver ? 'border-accent bg-accent/5' : 'border-border-strong hover:border-accent/50'}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pem,.crt,.cer,.key,.txt"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
        />
        {fileName ? (
          <>
            <FileCheck2 size={22} className="text-emerald-400" />
            <p className="text-sm text-text-secondary">{fileName}</p>
          </>
        ) : (
          <>
            <UploadCloud size={22} className="text-text-muted" />
            <p className="text-sm text-text-muted text-center">Arrastrá el archivo acá o hacé click para elegirlo</p>
          </>
        )}
      </div>
      {hint && !error && <p className="text-xs text-text-muted mt-1">{hint}</p>}
      {error && <p className="text-xs text-red-400 mt-1 flex items-center gap-1"><AlertTriangle size={12} /> {error}</p>}
    </div>
  );
}
